import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../../lib/api";

/**
 * O · Cloche de notifications in-app.
 *
 * Pastille de comptage des non-lues (refresh 30s), dropdown avec liste
 * 50 dernières, click = mark-as-read + navigation.
 */
export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const qc = useQueryClient();

  const unread = useQuery({
    queryKey: ["notif-unread"],
    queryFn: () => api.inAppNotifications.unreadCount(),
    refetchInterval: 30_000,
  });

  const list = useQuery({
    queryKey: ["notif-list"],
    queryFn: () => api.inAppNotifications.list(),
    enabled: open,
  });

  const readOne = useMutation({
    mutationFn: (id: string) => api.inAppNotifications.markOneRead(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["notif-unread"] });
      qc.invalidateQueries({ queryKey: ["notif-list"] });
    },
  });

  const readAll = useMutation({
    mutationFn: () => api.inAppNotifications.markAllRead(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["notif-unread"] });
      qc.invalidateQueries({ queryKey: ["notif-list"] });
    },
  });

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const count = unread.data?.count ?? 0;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="relative inline-flex h-9 w-9 items-center justify-center rounded-full text-navy-700 transition-colors hover:bg-navy-100"
        aria-label="Notifications"
        title="Notifications"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
        {count > 0 ? (
          <span className="absolute -right-0.5 -top-0.5 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
            {count > 99 ? "99+" : count}
          </span>
        ) : null}
      </button>
      {open ? (
        <div className="absolute right-0 z-40 mt-2 w-80 overflow-hidden rounded-2xl bg-white shadow-xl ring-1 ring-navy-100">
          <div className="flex items-center justify-between border-b border-navy-100 px-4 py-2">
            <span className="text-xs font-bold uppercase tracking-wider text-navy-500">
              Notifications
            </span>
            {count > 0 ? (
              <button
                type="button"
                onClick={() => readAll.mutate()}
                className="text-xs font-semibold text-accent-600 hover:underline"
              >
                Tout marquer lu
              </button>
            ) : null}
          </div>
          <div className="max-h-[60vh] overflow-y-auto">
            {list.isLoading ? (
              <p className="px-4 py-6 text-center text-sm text-navy-400">
                Chargement…
              </p>
            ) : (list.data ?? []).length === 0 ? (
              <p className="px-4 py-6 text-center text-sm text-navy-400">
                Aucune notification ✨
              </p>
            ) : (
              <ul className="divide-y divide-navy-100">
                {(list.data ?? []).map((n) => (
                  <li key={n.id}>
                    <button
                      type="button"
                      onClick={() => {
                        if (!n.lueAt) readOne.mutate(n.id);
                        if (n.url) navigate(n.url);
                        setOpen(false);
                      }}
                      className={`w-full px-4 py-3 text-left transition-colors hover:bg-navy-50 ${
                        !n.lueAt ? "bg-accent-50/30" : ""
                      }`}
                    >
                      <div className="flex items-start gap-2">
                        <span
                          className={`mt-1.5 inline-flex h-2 w-2 shrink-0 rounded-full ${
                            !n.lueAt ? "bg-accent-500" : "bg-transparent"
                          }`}
                        />
                        <div className="min-w-0 flex-1">
                          <p className="font-semibold text-navy-800">
                            {n.titre}
                          </p>
                          {n.body ? (
                            <p className="mt-0.5 text-xs text-navy-500 line-clamp-2">
                              {n.body}
                            </p>
                          ) : null}
                          <p className="mt-1 text-[10px] text-navy-400">
                            {timeAgo(n.createdAt)}
                          </p>
                        </div>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function timeAgo(iso: string): string {
  const t = new Date(iso).getTime();
  const ms = Date.now() - t;
  const sec = Math.floor(ms / 1000);
  if (sec < 60) return "à l'instant";
  const min = Math.floor(sec / 60);
  if (min < 60) return `il y a ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `il y a ${h} h`;
  const d = Math.floor(h / 24);
  if (d < 7) return `il y a ${d} j`;
  return new Date(iso).toLocaleDateString("fr-FR");
}
